import React, { useState, useEffect, useRef } from 'react';
import { formatDateDisplay } from '../utils/date';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function ErrorLogsDashboard() {
  const [errorLogs, setErrorLogs] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [componentFilter, setComponentFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'pending', 'resolved'

  // Selection states
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [lastClickedIndex, setLastClickedIndex] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Expanded error
  const [expandedErrorId, setExpandedErrorId] = useState(null);

  // Get auth token helper
  const getAuthToken = async () => {
    const { default: client } = await import('../supabaseClient');
    const { data: { session } } = await client.auth.getSession();
    return session?.access_token;
  };

  useEffect(() => {
    fetchErrorLogs();
    fetchStatistics();
  }, [searchQuery, componentFilter, startDate, endDate, sortBy, sortOrder, limit, offset, statusFilter]);

  // Clear selection when filters change
  useEffect(() => {
    setSelectedIds(new Set());
    setLastClickedIndex(null);
  }, [statusFilter, searchQuery, componentFilter, startDate, endDate]);

  const fetchErrorLogs = async () => {
    setLoading(true);
    try {
      const token = await getAuthToken();
      const params = new URLSearchParams({
        limit,
        offset,
        sortBy,
        sortOrder,
        ...(searchQuery && { searchQuery }),
        ...(componentFilter && { componentName: componentFilter }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(statusFilter !== 'all' && { resolvedStatus: statusFilter })
      });

      const response = await fetch(
        `${API_URL}/api/admin/error-logs?${params}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const data = await response.json();
      setErrorLogs(data.data.logs);
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch error logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const token = await getAuthToken();
      const response = await fetch(
        `${API_URL}/api/admin/error-logs/statistics`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const data = await response.json();
      setStatistics(data.data);
    } catch (err) {
      console.error('Failed to fetch statistics:', err);
    }
  };

  // Handle checkbox click with shift support
  const handleCheckboxClick = (logId, index, event) => {
    const newSelected = new Set(selectedIds);

    if (event.shiftKey && lastClickedIndex !== null) {
      // Shift+click: select range
      const start = Math.min(lastClickedIndex, index);
      const end = Math.max(lastClickedIndex, index);

      for (let i = start; i <= end; i++) {
        newSelected.add(errorLogs[i].id);
      }
    } else {
      // Regular click: toggle single
      if (newSelected.has(logId)) {
        newSelected.delete(logId);
      } else {
        newSelected.add(logId);
      }
    }

    setSelectedIds(newSelected);
    setLastClickedIndex(index);
  };

  // Select/deselect all
  const handleSelectAll = () => {
    if (selectedIds.size === errorLogs.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(errorLogs.map(log => log.id)));
    }
  };

  // Update resolved status
  const handleUpdateResolvedStatus = async (isResolved) => {
    if (selectedIds.size === 0) return;

    setIsUpdating(true);
    try {
      const token = await getAuthToken();
      const response = await fetch(
        `${API_URL}/api/admin/error-logs/resolve`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            errorIds: Array.from(selectedIds),
            isResolved
          })
        }
      );

      const data = await response.json();
      if (data.success) {
        setSelectedIds(new Set());
        fetchErrorLogs();
        fetchStatistics();
      } else {
        alert('Failed to update: ' + (data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Failed to update resolved status:', err);
      alert('Failed to update: ' + err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const getSeverityColor = (error) => {
    if (error.http_status_code === 500) return '#ef4444';
    if (error.http_status_code === 400) return '#f59e0b';
    return '#3b82f6';
  };

  const getSeverityLabel = (error) => {
    if (error.http_status_code === 500) return 'Critical';
    if (error.http_status_code === 400) return 'High';
    return 'Info';
  };

  const styles = {
    container: {
      padding: 'var(--space-6)',
      maxWidth: '1400px',
      margin: '0 auto'
    },
    header: {
      marginBottom: 'var(--space-6)'
    },
    title: {
      fontSize: 'var(--font-size-2xl)',
      fontWeight: 'var(--font-weight-bold)',
      margin: 0,
      marginBottom: 'var(--space-4)',
      color: 'var(--color-text-primary)'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: 'var(--space-4)',
      marginBottom: 'var(--space-6)'
    },
    statCard: {
      padding: 'var(--space-4)',
      borderRadius: 'var(--radius-lg)',
      backgroundColor: 'var(--color-bg-base)',
      border: '1px solid var(--color-border-light)',
      boxShadow: 'var(--shadow-sm)'
    },
    statValue: {
      fontSize: 'var(--font-size-2xl)',
      fontWeight: 'var(--font-weight-bold)',
      color: 'var(--color-primary)',
      marginBottom: 'var(--space-1)'
    },
    statLabel: {
      fontSize: 'var(--font-size-xs)',
      color: 'var(--color-text-secondary)',
      fontWeight: 'var(--font-weight-medium)'
    },
    filterSection: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
      gap: 'var(--space-3)',
      marginBottom: 'var(--space-4)',
      padding: 'var(--space-4)',
      backgroundColor: 'var(--color-bg-subtle)',
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--color-border-light)'
    },
    input: {
      padding: 'var(--space-2) var(--space-3)',
      borderRadius: 'var(--radius-sm)',
      border: '1px solid var(--color-border-default)',
      fontSize: 'var(--font-size-sm)',
      fontFamily: 'inherit',
      outline: 'none'
    },
    actionBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 'var(--space-3) var(--space-4)',
      backgroundColor: 'var(--color-bg-muted)',
      borderRadius: 'var(--radius-lg)',
      marginBottom: 'var(--space-4)',
      flexWrap: 'wrap',
      gap: 'var(--space-3)'
    },
    actionButton: {
      padding: 'var(--space-2) var(--space-4)',
      borderRadius: 'var(--radius-sm)',
      border: 'none',
      cursor: 'pointer',
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-medium)',
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-2)'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: 'var(--space-4)'
    },
    th: {
      padding: 'var(--space-3)',
      textAlign: 'left',
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-semibold)',
      backgroundColor: 'var(--color-bg-muted)',
      borderBottom: '2px solid var(--color-border-default)',
      color: 'var(--color-text-primary)'
    },
    td: {
      padding: 'var(--space-3)',
      borderBottom: '1px solid var(--color-border-light)',
      fontSize: 'var(--font-size-sm)'
    },
    errorRow: {
      backgroundColor: 'var(--color-bg-base)',
      transition: 'var(--transition-normal)'
    },
    errorMessage: {
      maxWidth: '350px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      color: 'var(--color-text-primary)'
    },
    severity: {
      padding: 'var(--space-1) var(--space-2)',
      borderRadius: 'var(--radius-sm)',
      fontSize: 'var(--font-size-xs)',
      fontWeight: 'var(--font-weight-semibold)'
    },
    statusBadge: {
      padding: '2px 8px',
      borderRadius: '10px',
      fontSize: '11px',
      fontWeight: '500'
    },
    expandButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontSize: '16px',
      padding: 0,
      color: 'var(--color-primary)'
    },
    expandedContent: {
      padding: 'var(--space-4)',
      backgroundColor: 'var(--color-bg-subtle)',
      borderLeft: '4px solid var(--color-primary)'
    },
    details: {
      marginBottom: 'var(--space-3)',
      fontSize: 'var(--font-size-sm)'
    },
    detailLabel: {
      fontWeight: 'var(--font-weight-semibold)',
      color: 'var(--color-text-primary)',
      marginRight: 'var(--space-2)'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      gap: 'var(--space-2)',
      marginTop: 'var(--space-6)'
    },
    paginationButton: {
      padding: 'var(--space-2) var(--space-3)',
      borderRadius: 'var(--radius-sm)',
      border: '1px solid var(--color-border-default)',
      backgroundColor: 'var(--color-bg-base)',
      cursor: 'pointer',
      fontSize: 'var(--font-size-sm)',
      transition: 'var(--transition-normal)'
    },
    checkbox: {
      width: '18px',
      height: '18px',
      cursor: 'pointer',
      accentColor: '#3b82f6'
    }
  };

  if (loading && errorLogs.length === 0) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: 'var(--space-10)' }}>
          <div style={{ fontSize: '48px', marginBottom: 'var(--space-4)' }}>...</div>
          <p>Loading error logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Error Logs Dashboard</h2>
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', margin: 0 }}>
          Monitor and analyze errors across the application
        </p>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{statistics.totalErrors || 0}</div>
            <div style={styles.statLabel}>Total Errors</div>
          </div>
          <div style={{ ...styles.statCard, borderLeft: '3px solid #f59e0b' }}>
            <div style={{ ...styles.statValue, color: '#f59e0b' }}>{statistics.pendingErrors || 0}</div>
            <div style={styles.statLabel}>Pending</div>
          </div>
          <div style={{ ...styles.statCard, borderLeft: '3px solid #10b981' }}>
            <div style={{ ...styles.statValue, color: '#10b981' }}>{statistics.resolvedErrors || 0}</div>
            <div style={styles.statLabel}>Resolved</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{statistics.totalErrorsLast24h || 0}</div>
            <div style={styles.statLabel}>Last 24h</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{statistics.uniqueUsersAffected || 0}</div>
            <div style={styles.statLabel}>Users Affected</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={styles.filterSection}>
        <input
          type="text"
          placeholder="Search errors..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setOffset(0);
          }}
          style={styles.input}
        />
        <input
          type="text"
          placeholder="Component"
          value={componentFilter}
          onChange={(e) => {
            setComponentFilter(e.target.value);
            setOffset(0);
          }}
          style={styles.input}
        />
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setOffset(0);
          }}
          style={styles.input}
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="resolved">Resolved</option>
        </select>
        <input
          type="date"
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
            setOffset(0);
          }}
          style={styles.input}
        />
        <input
          type="date"
          value={endDate}
          onChange={(e) => {
            setEndDate(e.target.value);
            setOffset(0);
          }}
          style={styles.input}
        />
        <select
          value={limit}
          onChange={(e) => {
            setLimit(parseInt(e.target.value));
            setOffset(0);
          }}
          style={styles.input}
        >
          <option value="25">25/page</option>
          <option value="50">50/page</option>
          <option value="100">100/page</option>
        </select>
      </div>

      {/* Action Bar */}
      {errorLogs.length > 0 && (
        <div style={styles.actionBar}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
              {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select errors to mark as resolved'}
            </span>
            <span style={{ fontSize: '11px', color: '#A1A1AA' }}>
              (Shift+click for range)
            </span>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              onClick={() => handleUpdateResolvedStatus(true)}
              disabled={selectedIds.size === 0 || isUpdating}
              style={{
                ...styles.actionButton,
                backgroundColor: selectedIds.size > 0 ? '#10b981' : '#E0E0E0',
                color: 'white',
                cursor: selectedIds.size === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              {isUpdating ? 'Updating...' : 'Mark Resolved'}
            </button>
            <button
              onClick={() => handleUpdateResolvedStatus(false)}
              disabled={selectedIds.size === 0 || isUpdating}
              style={{
                ...styles.actionButton,
                backgroundColor: selectedIds.size > 0 ? '#f59e0b' : '#E0E0E0',
                color: 'white',
                cursor: selectedIds.size === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              {isUpdating ? 'Updating...' : 'Mark Pending'}
            </button>
          </div>
        </div>
      )}

      {/* Error Logs Table */}
      {errorLogs.length > 0 ? (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.size === errorLogs.length && errorLogs.length > 0}
                      onChange={handleSelectAll}
                      style={styles.checkbox}
                      title="Select all"
                    />
                  </th>
                  <th style={styles.th}>Date & Time</th>
                  <th style={styles.th}>Error Message</th>
                  <th style={styles.th}>Component</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Severity</th>
                  <th style={{ ...styles.th, width: '50px' }}></th>
                </tr>
              </thead>
              <tbody>
                {errorLogs.map((log, index) => (
                  <React.Fragment key={log.id}>
                    <tr style={{
                      ...styles.errorRow,
                      backgroundColor: selectedIds.has(log.id) ? '#eff6ff' : 'var(--color-bg-base)'
                    }}>
                      <td style={styles.td}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(log.id)}
                          onChange={(e) => handleCheckboxClick(log.id, index, e)}
                          onClick={(e) => e.stopPropagation()}
                          style={styles.checkbox}
                        />
                      </td>
                      <td style={styles.td}>
                        {formatDateDisplay(log.created_at)}
                      </td>
                      <td style={{ ...styles.td, ...styles.errorMessage }}>
                        {log.error_message}
                      </td>
                      <td style={styles.td}>{log.component_name || '-'}</td>
                      <td style={styles.td}>
                        <span style={{
                          ...styles.statusBadge,
                          backgroundColor: log.is_resolved ? '#dcfce7' : '#fef3c7',
                          color: log.is_resolved ? '#166534' : '#92400e'
                        }}>
                          {log.is_resolved ? 'Resolved' : 'Pending'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={{
                          ...styles.severity,
                          backgroundColor: `${getSeverityColor(log)}20`,
                          color: getSeverityColor(log)
                        }}>
                          {getSeverityLabel(log)}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <button
                          onClick={() => setExpandedErrorId(expandedErrorId === log.id ? null : log.id)}
                          style={styles.expandButton}
                          title={expandedErrorId === log.id ? 'Collapse' : 'Expand'}
                        >
                          {expandedErrorId === log.id ? '...' : '...'}
                        </button>
                      </td>
                    </tr>
                    {expandedErrorId === log.id && (
                      <tr>
                        <td colSpan="7" style={{ padding: 0 }}>
                          <div style={styles.expandedContent}>
                            <div style={styles.details}>
                              <span style={styles.detailLabel}>Page:</span>
                              {log.page_name || 'Unknown'}
                            </div>
                            <div style={styles.details}>
                              <span style={styles.detailLabel}>Action:</span>
                              {log.user_action || 'Unknown'}
                            </div>
                            <div style={styles.details}>
                              <span style={styles.detailLabel}>URL:</span>
                              {log.request_url || 'Unknown'}
                            </div>
                            {log.resolved_at && (
                              <div style={styles.details}>
                                <span style={styles.detailLabel}>Resolved At:</span>
                                {formatDateDisplay(log.resolved_at)}
                              </div>
                            )}
                            {log.error_stack && (
                              <div style={styles.details}>
                                <span style={styles.detailLabel}>Stack Trace:</span>
                                <pre style={{
                                  margin: 'var(--space-2) 0 0 0',
                                  padding: 'var(--space-2)',
                                  backgroundColor: 'var(--color-bg-base)',
                                  borderRadius: 'var(--radius-sm)',
                                  fontSize: 'var(--font-size-xs)',
                                  overflow: 'auto',
                                  maxHeight: '200px'
                                }}>
                                  {log.error_stack}
                                </pre>
                              </div>
                            )}
                            {log.additional_details && (
                              <div style={styles.details}>
                                <span style={styles.detailLabel}>Details:</span>
                                <pre style={{
                                  margin: 'var(--space-2) 0 0 0',
                                  padding: 'var(--space-2)',
                                  backgroundColor: 'var(--color-bg-base)',
                                  borderRadius: 'var(--radius-sm)',
                                  fontSize: 'var(--font-size-xs)',
                                  overflow: 'auto'
                                }}>
                                  {JSON.stringify(log.additional_details, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={styles.pagination}>
            <button
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={offset === 0}
              style={{
                ...styles.paginationButton,
                opacity: offset === 0 ? 0.5 : 1,
                cursor: offset === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              Previous
            </button>
            <span style={{ padding: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>
              Page {offset / limit + 1}
            </span>
            <button
              onClick={() => setOffset(offset + limit)}
              style={styles.paginationButton}
            >
              Next
            </button>
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: 'var(--space-10)', color: 'var(--color-text-secondary)' }}>
          <div style={{ fontSize: '48px', marginBottom: 'var(--space-4)' }}>...</div>
          <p>No errors found{statusFilter !== 'all' ? ` with status "${statusFilter}"` : ''}</p>
        </div>
      )}
    </div>
  );
}

export default ErrorLogsDashboard;
